import React, { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import { assets } from '@yola/ws-sdk';
import contextMenu from 'src/js/modules/context-menu';
import UploadImageMenu from 'src/js/modules/common/components/upload-image-menu';
import getMenuCssPosition from 'src/js/modules/context-menu/helpers/get-menu-css-position';

const { ImagePickerPreview } = designSystem;
const VERTICAL_GAP = 6;

function ChooseImageButton({
  label,
  placeholder,
  fileItemName,
  statusText,
  onImageUpload,
  onStockPhotoUpload,
  onWrongFileType,
  onUrlPhotoUpload,
  onUrlPhotoUploadCancel,
  onStockPhotoDialogCancel,
  onClear,
}) {
  const triggerNodeRef = useRef(null);

  const preventNativeFilePickerOpen = useCallback((event) => {
    event.preventDefault();
  }, []);

  const calculatePositionDecorator = useCallback((menuElement) => {
    if (!triggerNodeRef.current) {
      return getMenuCssPosition(menuElement, 0, 0);
    }

    const { x, y } = triggerNodeRef.current.getBoundingClientRect();
    const { height } = menuElement.getBoundingClientRect();
    const eventY = y - height / 2 - VERTICAL_GAP;

    return getMenuCssPosition(menuElement, x, eventY);
  }, []);

  const getContextMenuPosition = useCallback(() => {
    if (!triggerNodeRef.current) {
      return { x: 0, y: 0 };
    }

    const { x, y } = triggerNodeRef.current.getBoundingClientRect();
    return { x, y };
  }, []);

  const handleClick = useCallback(() => {
    const tabContent = triggerNodeRef?.current?.closest('.ws-ui-tab-content');
    if (!tabContent) return;

    tabContent.addEventListener(
      'scroll',
      () => {
        contextMenu.operations.hideContextMenu();
      },
      { once: true }
    );
  }, []);

  return (
    <UploadImageMenu
      id="page-settings-open-graph-image-upload"
      accept={assets.constants.IMAGE_ACCEPT_TYPE}
      enableAddFromUrl
      triggerNodeRef={triggerNodeRef}
      getContextMenuPosition={getContextMenuPosition}
      calculatePositionDecorator={calculatePositionDecorator}
      onImageUpload={onImageUpload}
      onStockPhotoUpload={onStockPhotoUpload}
      onWrongFileType={onWrongFileType}
      onUrlPhotoUpload={onUrlPhotoUpload}
      onUrlPhotoUploadCancel={onUrlPhotoUploadCancel}
      onPhotoStockDialogCancel={onStockPhotoDialogCancel}
    >
      <div
        onMouseDownCapture={preventNativeFilePickerOpen}
        onClickCapture={(event) => {
          handleClick();
          preventNativeFilePickerOpen(event);
        }}
      >
        <ImagePickerPreview
          size="medium"
          label={label}
          placeholder={placeholder}
          fileItemName={fileItemName}
          statusText={statusText}
          onClear={onClear}
        />
      </div>
    </UploadImageMenu>
  );
}

ChooseImageButton.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  fileItemName: PropTypes.string.isRequired,
  statusText: PropTypes.string.isRequired,
  onImageUpload: PropTypes.func.isRequired,
  onStockPhotoUpload: PropTypes.func.isRequired,
  onWrongFileType: PropTypes.func.isRequired,
  onUrlPhotoUpload: PropTypes.func.isRequired,
  onUrlPhotoUploadCancel: PropTypes.func.isRequired,
  onStockPhotoDialogCancel: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

export default ChooseImageButton;
